import { useQuery } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import parse, {
  domToReact,
  Element,
  type HTMLReactParserOptions,
} from 'html-react-parser';
import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import Showdown from 'showdown';

import IconButton from '@/components/Button/IconButton';
import { ToggleStage } from '@/components/Stage/ToggleStage';
import { CitationBadge, MessageTextStyle } from '@/entities/hey-syncly';
import { getIssueSummary } from '@/entities/issue';
import { useFeedbackTerminology } from '@/entities/workspace/hooks';
import { openSignalReferencesPreviewDialog } from '@/features/(signal)/signal-issue-detail/ui/signal-feedback-preview-dialog';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { STATIC_ISSUE_SUMMARY_DATA_KEY } from '../model/constants';

export const IssueDetailSummary = (props: {
  filterSet: StructuredFilter;
  issue_name: string;
}) => {
  // ({ workspace_id, ...payload })
  const { data, isFetching, refetch } = useQuery({
    queryKey: ['issue-summary', props.issue_name, props.filterSet],
    queryFn: () =>
      getIssueSummary({
        workspace_id: getCurrWorkspaceAlias(),
        name: props.issue_name,
        filter_set: props.filterSet,
      }),
  });

  useEffect(() => {
    if (data) {
      localStorage.setItem(STATIC_ISSUE_SUMMARY_DATA_KEY, JSON.stringify(data));
    }
  }, [data]);

  return (
    <IssueDetailSummaryContent
      summary={data?.body}
      references={data?.references || []}
      isFetching={isFetching}
      refetch={refetch}
    />
  );
};

export const IssueDetailSummaryContent = (props: {
  summary?: string;
  references: {
    citation_id: string;
    feedback_id: string;
    block_id: string[];
  }[];
  isFetching?: boolean;
  refetch?: () => void;
}) => {
  const parseOptions = useParseOptions({
    references: props.references || [],
  });
  const { summary, isFetching, refetch } = props;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-semibold">Overview</h3>
        {!isFetching && (
          <IconButton
            icon={<RefreshCw className="size-4" />}
            tooltip="Refresh"
            onClick={refetch}
            outlined={false}
          />
        )}
      </div>
      <div className="flex flex-col gap-4">
        <ToggleStage
          className="px-6 py-5 rounded-2xl border border-border"
          status={!isFetching}
          fallback={
            <div className="flex flex-col gap-2">
              <Skeleton.Node
                style={{
                  width: 400,
                  height: 24,
                }}
                active
              />
              <Skeleton.Node
                style={{
                  width: '100%',
                  height: 24,
                }}
                active
              />
              <Skeleton.Node
                style={{
                  width: '100%',
                  height: 24,
                }}
                active
              />
            </div>
          }
          data={summary}
        >
          {(summary) => (
            <MessageTextStyle>
              {parse(
                formatMessageWithCitations({
                  text: summary,
                  references: props.references,
                }),
                parseOptions,
              )}
            </MessageTextStyle>
          )}
        </ToggleStage>
      </div>
    </div>
  );
};

const converter = new Showdown.Converter({
  tables: true,
  literalMidWordUnderscores: true,
  simpleLineBreaks: true,
  disableForced4SpacesIndentedSublists: true,
});

/*
 * @description
 * 다음과 같은 예외 사항들을 커버하는 regex
 * <citation id="1,2,3,4,5"/> - 정상포맷
 * <citation id="1,2,3,4,5/> - 뒷 따옴표
 * <citation id='1,2,3,4,5'/> - 큰 따옴표 대신 작은 따옴표
 * <citation id=1,2,3,4,5 /> - 따옴표 전체 누락 + 공백
 * <citation id=1,2,3,4,5/> 따옴표 전체 누락
 */
const citationRegex = /<citation\s+id=["']?([^"'>/]+)["']?\s*\/>/g;
const citationStartPattern = /<citation[^>]*$/;

const formatMessageWithCitations = (message: {
  text: string;
  message_id?: Alias;
  references?: {
    citation_id: string;
    feedback_id: string;
    block_id: string[];
  }[];
  hideCitation?: boolean;
}) => {
  if (!message.text) return '';

  let text = message.text;

  const incompleteMatch = citationStartPattern.exec(text);
  if (incompleteMatch) {
    text = text.slice(0, incompleteMatch.index);
  }

  text = fixCitationTags(text);

  const textWithHtmlCitations = message.hideCitation
    ? text.replace(citationRegex, '')
    : text.replace(citationRegex, (match, ids) => {
        const idArray: string[] = ids.split(',').map((id: string) => id.trim());
        const feedbacks = idArray.reduce((acc, value) => {
          const target = message.references?.find(
            (ref) => ref.citation_id === value,
          );
          if (target && !acc.includes(target.feedback_id)) {
            acc.push(target.feedback_id);
          }
          return acc;
        }, [] as string[]);

        return `<a href="#" class="citation" data-ids="${ids}" data-message-id="${message.message_id}">${feedbacks.length}</a>`;
      });

  const fullHtml = converter
    .makeHtml(textWithHtmlCitations)
    .replace(/<\/?p>/g, '<br>')
    .replace(/<li><br>/g, '<li>')
    .replace(/<br>\s*<\/li>/g, '</li>')
    .replace(/^(<br\s*\/?>\s*)+/i, '')
    .replaceAll('\\n', '');

  return fullHtml;
};

export const stripCitations = (text: string) => {
  const strippedText = text.replace(citationRegex, '');
  return strippedText;
};

export const fixCitationTags = (text: string) => {
  const pattern = new RegExp(
    String.raw`(<citation id=")([^\]/)]*)(/\]|/\)|\]|\))`,
    'g',
  );

  const replacement = '$1$2"/>';

  return text.replace(pattern, replacement);
};

const useParseOptions = ({
  references,
}: {
  references: {
    citation_id: string;
    feedback_id: string;
    block_id: string[];
  }[];
}) => {
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const [previewDialogKey, setPreviewDialogKey] = useState<string | null>(null);

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (
        domNode instanceof Element &&
        domNode.name === 'a' &&
        domNode.attribs.class === 'citation'
      ) {
        const ids = domNode.attribs['data-ids'].split(',');

        const key = JSON.stringify({
          citation_ids: ids,
        });

        const payload = references.reduce(
          (acc, curr) => {
            ids.includes(curr.citation_id) &&
              acc.push({
                feedback_id: curr.feedback_id,
                block_id: curr.block_id,
              });
            return acc;
          },
          [] as { feedback_id: string; block_id: string[] }[],
        );

        return (
          <CitationBadge
            selected={previewDialogKey === key}
            onClick={(e) => {
              e.preventDefault();
              if (previewDialogKey === key) return;

              setPreviewDialogKey(key);
              openSignalReferencesPreviewDialog({
                title: replaceFeedbackTerminology('Referenced posts'),
                queryKey: ['signal', 'summary', key],
                references: payload,
                onExitComplete: () => {
                  setPreviewDialogKey((currentKey) =>
                    currentKey === key ? null : currentKey,
                  );
                },
              });
            }}
          >
            {domToReact(domNode.children as any, options)}
          </CitationBadge>
        );
      }
    },
  };

  return options;
};
